import React, {
    useState,
    useEffect,
    useContext,
    createContext,
    useCallback,
    useRef,
    useMemo,
} from 'react';
import axios from 'axios';
import cookie from 'js-cookie';
import { useParams, useLocation } from 'react-router-dom';
import {
    Typography,
    FormControlLabel,
    Switch,
    Box,
    CircularProgress,
    Fade,
    TextField,
    Alert,
    Snackbar,
    Paper,
    Tabs,
    Tab,
    Skeleton,
    Chip,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { styled } from '@mui/material/styles';
import {
    NotificationPreferences,
    NotificationProvider,
    useNotifications,
} from '../../../../NotificationSystem';
import Breadcrumbs from '../../../global/Components/Breadcrumbs';
import { fetchProjectById, updateProject } from '../services/projectDashboardService';
import ManageUsers from './components/ManageUsers';
import FieldWorkerRequests from './components/FieldWorkerRequests';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';
import { isExternalSubcontractorRole, isExternalRole } from '../../../../utils/roles';

// Floating save indicator for when header is out of view
const FloatingSaveIndicator = styled(Paper)(({ theme }) => ({
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    borderRadius: '8px',
    zIndex: 1000,
    border: `1px solid ${theme.palette.divider}`,
    minWidth: '120px',
    [theme.breakpoints.down('sm')]: {
        bottom: '16px',
        right: '16px',
        padding: '10px 14px',
        minWidth: '100px',
    },
}));

// Context for managing unified save state across the page
const PageSaveContext = createContext();

/**
 * Dedicated page for editing project settings
 * All settings auto-save when changed with unified save feedback
 */
const ProjectSettingsPage = () => {
    const { projectId } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { canProject, getProjectRole, isLoading: permissionsLoading } = usePermissions();

    // Permission checks
    const userRole = getProjectRole(projectId);
    const canWriteProject = canProject(projectId, PERMISSIONS.PROJECT_WRITE);
    const canReadMembers = canProject(projectId, PERMISSIONS.PROJECT_MEMBERSHIP_READ);
    const canWriteMembers = canProject(projectId, PERMISSIONS.PROJECT_MEMBERSHIP_WRITE);
    const canViewMembersTab = (canReadMembers || canWriteMembers) && !isExternalRole(userRole);
    const canReadFieldWorkerRequests = canProject(projectId, PERMISSIONS.FIELD_WORKER_REQUEST_READ);
    const canWriteFieldWorkerRequests = canProject(
        projectId,
        PERMISSIONS.FIELD_WORKER_REQUEST_WRITE
    );
    const canReviewFieldWorkerRequests = canProject(
        projectId,
        PERMISSIONS.FIELD_WORKER_REQUEST_REVIEW
    );
    const canManageNotificationPreferences = !isExternalSubcontractorRole(userRole);
    const canViewOverviewTab = canWriteProject || canManageNotificationPreferences;

    // Build dynamic tabs based on permissions
    const tabs = useMemo(() => {
        if (permissionsLoading) return [];
        const tabList = [];
        if (canViewOverviewTab) {
            tabList.push({ label: 'Overview', id: 'overview' });
        }
        if (canViewMembersTab) {
            tabList.push({ label: 'Manage Project Members', id: 'manage-users' });
        }
        if (canReadFieldWorkerRequests) {
            const fieldWorkerTabLabel = canReviewFieldWorkerRequests
                ? 'Crew Add Requests'
                : 'Manage your crew';
            tabList.push({ label: fieldWorkerTabLabel, id: 'field-workers' });
        }
        return tabList;
    }, [
        permissionsLoading,
        canViewMembersTab,
        canViewOverviewTab,
        canReadFieldWorkerRequests,
        canReviewFieldWorkerRequests,
    ]);

    // Project data and loading state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [project, setProject] = useState(null);
    const [currentUserName, setCurrentUserName] = useState('');

    // Unified save state for the entire page
    const [isSaving, setIsSaving] = useState(false);
    const [showSaved, setShowSaved] = useState(false);
    const [wasUpdating, setWasUpdating] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);

    // Tab state
    const [tabValue, setTabValue] = useState(0);
    const location = useLocation();

    // Handle navigation state to open a specific tab (e.g., from notification click)
    useEffect(() => {
        if (location.state?.selectedTab && tabs.length > 0) {
            const targetIndex = tabs.findIndex((tab) => tab.id === location.state.selectedTab);
            if (targetIndex >= 0) {
                setTabValue(targetIndex);
            }
        }
    }, [location.state?.selectedTab, tabs]);

    // Role check for PM-only settings
    const isPMOrAdmin = userRole === 'INTERNAL_PM' || userRole === 'INTERNAL_ADMIN';

    // Local state for project settings
    const [isActive, setIsActive] = useState(false);
    const [fieldId, setFieldId] = useState('');
    const [isShareWithExternal, setIsShareWithExternal] = useState(false);
    const [isAllowExternalSubmissions, setIsAllowExternalSubmissions] = useState(false);
    const [externalSubmissionDeadline, setExternalSubmissionDeadline] = useState('');
    const [autoDueDateEnabled, setAutoDueDateEnabled] = useState(false);
    const [autoDueDateDays, setAutoDueDateDays] = useState('');
    const [floorplanGroupingEnabled, setFloorplanGroupingEnabled] = useState(false);
    const [buildingNames, setBuildingNames] = useState([]);

    // Snackbar for error handling
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Load project data
    useEffect(() => {
        const loadProject = async () => {
            try {
                setLoading(true);
                const projectData = await fetchProjectById(projectId);
                setProject(projectData);

                // Initialize state with project data
                setIsActive(projectData.status === 'Active');
                setFieldId(projectData.fieldId || '');
                setIsShareWithExternal(projectData.shareInternalDeficienciesExternally || false);
                setIsAllowExternalSubmissions(projectData.allowExternalSubmissions || false);
                setExternalSubmissionDeadline(projectData.externalSubmissionDeadline || '');
                setAutoDueDateEnabled(projectData.autoDueDateEnabled || false);
                setAutoDueDateDays(
                    projectData.autoDueDateDays != null ? String(projectData.autoDueDateDays) : ''
                );
                setFloorplanGroupingEnabled(projectData.floorplanGroupingEnabled || false);
                setBuildingNames(projectData.buildingNames || []);

                setError(null);
            } catch (err) {
                console.error('Error loading project:', err);
                setError('Failed to load project data. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (projectId) {
            loadProject();
        }
    }, [projectId]);

    // Load current user's name for personalized notification preferences
    useEffect(() => {
        const loadCurrentUserName = async () => {
            try {
                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');
                const userId = cookie.get('userId');

                if (!API_URL || !xsrfToken || !userId) return;

                const response = await axios.get(`${API_URL}/user/get-user/${userId}`, {
                    withCredentials: true,
                    headers: {
                        'X-XSRF-TOKEN': xsrfToken,
                    },
                });

                const { firstName, lastName } = response.data || {};
                const name = [firstName, lastName].filter(Boolean).join(' ').trim();
                if (name) setCurrentUserName(name);
            } catch (e) {
                // Non-blocking: silently ignore failures
            }
        };

        loadCurrentUserName();
    }, []);

    // Handle unified save state - this will be used by both project status and notifications
    const updateSaveState = (isCurrentlySaving) => {
        if (isCurrentlySaving) {
            setIsSaving(true);
            setWasUpdating(true);
        } else if (wasUpdating && !isCurrentlySaving) {
            setIsSaving(false);
            setWasUpdating(false);
            setShowSaved(true);

            // Clear the saved indicator after 3 seconds
            setTimeout(() => {
                setShowSaved(false);
            }, 3000);
        }
    };

    // Track project settings saving
    useEffect(() => {
        updateSaveState(savingSettings);
    }, [savingSettings, wasUpdating]);

    // Handle settings save
    const handleSettingsSave = async (updatedProject) => {
        try {
            setSavingSettings(true);
            const result = await updateProject(projectId, updatedProject);
            setProject(result);
        } catch (err) {
            console.error('Error updating project:', err);
            setSnackbar({
                open: true,
                message: 'Failed to update project settings',
                severity: 'error',
            });
            throw err; // Re-throw to handle in individual handlers
        } finally {
            setSavingSettings(false);
        }
    };

    // Handle project status toggle - auto-save
    const handleActiveToggle = async (event) => {
        const newActiveState = event.target.checked;
        setIsActive(newActiveState);

        try {
            const updatedProject = {
                ...project,
                isActive: newActiveState,
            };
            await handleSettingsSave(updatedProject);
        } catch (error) {
            // Revert toggle state on error
            setIsActive(!newActiveState);
        }
    };

    // Handle share with external toggle - auto-save
    const handleShareWithExternalToggle = async (event) => {
        const newShareState = event.target.checked;
        setIsShareWithExternal(newShareState);

        try {
            const updatedProject = {
                ...project,
                shareInternalDeficienciesExternally: newShareState,
            };
            await handleSettingsSave(updatedProject);
        } catch (error) {
            // Revert toggle state on error
            setIsShareWithExternal(!newShareState);
        }
    };

    // Handle allow external submissions toggle - auto-save
    const handleAllowExternalSubmissionsToggle = async (event) => {
        const newAllowSubmissionsState = event.target.checked;
        setIsAllowExternalSubmissions(newAllowSubmissionsState);

        try {
            const updatedProject = {
                ...project,
                allowExternalSubmissions: newAllowSubmissionsState,
            };
            await handleSettingsSave(updatedProject);
        } catch (error) {
            // Revert toggle state on error
            setIsAllowExternalSubmissions(!newAllowSubmissionsState);
        }
    };

    // Use refs to maintain latest values without recreating debounced function
    const projectRef = useRef(project);
    const handleSettingsSaveRef = useRef(handleSettingsSave);

    // Update refs when values change
    useEffect(() => {
        projectRef.current = project;
        handleSettingsSaveRef.current = handleSettingsSave;
    }, [project]);

    // Debounced save for project ID - stable function that uses refs
    const debouncedFieldIdSave = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(async () => {
                    try {
                        const trimmedValue = value.trim();
                        const currentProject = projectRef.current;
                        const currentHandleSettingsSave = handleSettingsSaveRef.current;

                        const updatedProject = {
                            ...currentProject,
                            fieldId: trimmedValue === '' ? null : trimmedValue,
                        };
                        await currentHandleSettingsSave(updatedProject);
                        // Reset touched flag after successful save
                    } catch (error) {
                        console.error('Failed to update project ID:', error);
                        setFieldId(projectRef.current?.fieldId || '');
                    }
                }, 1000); // 1 second delay
            };
        })(),
        []
    );

    // Handle project ID change - update local state immediately, save after delay
    const handleFieldIdChange = (event) => {
        const newFieldId = event.target.value;
        setFieldId(newFieldId);
        debouncedFieldIdSave(newFieldId);
    };

    // Handle external submissions deadline date change - auto-save
    const handleDeadlineDateChange = async (event) => {
        const newDeadlineDate = event.target.value;
        setExternalSubmissionDeadline(newDeadlineDate);

        try {
            const updatedProject = {
                ...project,
                externalSubmissionDeadline: newDeadlineDate,
            };
            await handleSettingsSave(updatedProject);
        } catch (error) {
            // Revert date state on error
            setExternalSubmissionDeadline(project?.externalSubmissionDeadline || '');
        }
    };

    // Handle auto due date toggle - auto-save
    const handleAutoDueDateToggle = async (event) => {
        const newEnabled = event.target.checked;
        setAutoDueDateEnabled(newEnabled);

        try {
            const updatedProject = {
                ...project,
                autoDueDateEnabled: newEnabled,
                // When disabling, preserve the days value so it's remembered if re-enabled
                autoDueDateDays: newEnabled
                    ? autoDueDateDays
                        ? parseInt(autoDueDateDays, 10)
                        : null
                    : project.autoDueDateDays,
            };
            await handleSettingsSave(updatedProject);
        } catch (error) {
            setAutoDueDateEnabled(!newEnabled);
        }
    };

    // Debounced save for auto due date days - stable function that uses refs
    const debouncedAutoDueDateDaysSave = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(async () => {
                    try {
                        const parsed = parseInt(value, 10);
                        if (isNaN(parsed) || parsed < 1) return;

                        const currentProject = projectRef.current;
                        const currentHandleSettingsSave = handleSettingsSaveRef.current;

                        const updatedProject = {
                            ...currentProject,
                            autoDueDateDays: parsed,
                        };
                        await currentHandleSettingsSave(updatedProject);
                    } catch (error) {
                        console.error('Failed to update auto due date days:', error);
                        setAutoDueDateDays(
                            projectRef.current?.autoDueDateDays != null
                                ? String(projectRef.current.autoDueDateDays)
                                : ''
                        );
                    }
                }, 1000);
            };
        })(),
        []
    );

    // Handle auto due date days change
    const handleAutoDueDateDaysChange = (event) => {
        const newValue = event.target.value;
        // Only allow positive integers
        if (newValue === '' || /^\d+$/.test(newValue)) {
            setAutoDueDateDays(newValue);
            if (newValue !== '' && parseInt(newValue, 10) > 0) {
                debouncedAutoDueDateDaysSave(newValue);
            }
        }
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Define breadcrumb links
    const breadcrumbLinks = [
        { path: '/projects', label: 'Projects' },
        { path: `/projects/${projectId}`, label: project?.name || 'Project Dashboard' },
        { path: `/projects/${projectId}/settings`, label: ' Project Settings' },
    ];

    // Context value for child components to report their save states
    const pageSaveContextValue = {
        updateSaveState,
    };

    if (loading) {
        return (
            <Box
                sx={{
                    py: 8,
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    boxSizing: 'border-box',
                }}
            >
                <CircularProgress size={32} sx={{ color: '#1652f0' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ py: 3, width: '100%', boxSizing: 'border-box' }}>
                <Alert severity="error" sx={{ borderRadius: '8px' }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <PageSaveContext.Provider value={pageSaveContextValue}>
            <Box
                sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    minHeight: 'calc(100vh - 64px)',
                }}
            >
                <Grid2 container spacing={{ xs: 2, sm: 3 }}>
                    {/* Header Section */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ mb: { xs: 1, sm: 1 } }}>
                            <Breadcrumbs links={breadcrumbLinks} />
                        </Box>
                    </Grid2>

                    {/* Tabs Section */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                            {permissionsLoading ? (
                                <Box sx={{ display: 'flex', gap: 2, pb: 1 }}>
                                    <Skeleton
                                        variant="rectangular"
                                        width={100}
                                        height={36}
                                        sx={{ borderRadius: 1 }}
                                    />
                                    <Skeleton
                                        variant="rectangular"
                                        width={180}
                                        height={36}
                                        sx={{ borderRadius: 1 }}
                                    />
                                </Box>
                            ) : (
                                <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    variant={isMobile ? 'scrollable' : 'standard'}
                                    scrollButtons={isMobile ? 'auto' : false}
                                    allowScrollButtonsMobile
                                    aria-label="project settings tabs"
                                    sx={{
                                        '& .MuiTab-root': {
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: { xs: '0.875rem', sm: '1rem' },
                                            minWidth: { xs: 'auto', sm: 90 },
                                            px: { xs: 1.5, sm: 2 },
                                        },
                                        '& .MuiTabs-scrollButtons': {
                                            '&.Mui-disabled': {
                                                opacity: 0.3,
                                            },
                                        },
                                    }}
                                >
                                    {tabs.map((tab) => (
                                        <Tab key={tab.id} label={tab.label} />
                                    ))}
                                </Tabs>
                            )}
                        </Box>
                    </Grid2>

                    {/* Settings Content */}
                    <Grid2 size={{ xs: 12 }}>
                        {tabs[tabValue]?.id === 'overview' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Auto-save message - only show when user can edit settings */}
                                {canWriteProject && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontStyle: 'italic', textAlign: 'left' }}
                                    >
                                        All changes are saved automatically.
                                    </Typography>
                                )}

                                {/* Section 1: General Settings - gated by PROJECT_WRITE */}
                                {canWriteProject && (
                                    <Box
                                        sx={{
                                            backgroundColor: '#fff',
                                            borderRadius: '12px',
                                            p: { xs: 3, sm: 4 },
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                            border: '1px solid #f0f0f0',
                                        }}
                                    >
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1,
                                                color: '#0a0b0d',
                                                fontSize: '1.25rem',
                                            }}
                                        >
                                            General
                                        </Typography>

                                        {/* Project Status Setting */}
                                        <Box
                                            sx={{
                                                py: 2,
                                                borderBottom: '1px solid #f0f0f0',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-start',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 600, mb: 0.5 }}
                                            >
                                                Project Status
                                            </Typography>

                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={isActive}
                                                        onChange={handleActiveToggle}
                                                        color="primary"
                                                        disabled={isSaving}
                                                    />
                                                }
                                                label={isActive ? 'Active' : 'Inactive'}
                                                sx={{ m: 0 }}
                                            />

                                            <Typography variant="body2" color="text.secondary">
                                                {isActive
                                                    ? 'Active projects are visible to all project members.'
                                                    : 'Inactive projects are archived and have limited functionality.'}
                                            </Typography>
                                        </Box>

                                        {/* Project ID Setting */}
                                        <Box
                                            sx={{
                                                py: 2,
                                                borderBottom: '1px solid #f0f0f0',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-start',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 600, mb: 0.5 }}
                                            >
                                                Project ID
                                            </Typography>

                                            <TextField
                                                label="Project ID (Optional)"
                                                value={fieldId}
                                                onChange={handleFieldIdChange}
                                                disabled={isSaving}
                                                fullWidth
                                                size="small"
                                                placeholder="Enter project ID..."
                                            />
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mb: 2 }}
                                            >
                                                A unique identifier for this project within your
                                                business
                                            </Typography>
                                        </Box>

                                        {/* Multi-Building (Read Only) */}
                                        <Box
                                            sx={{
                                                py: 2,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-start',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 600, mb: 0.5 }}
                                            >
                                                Multi-Building
                                            </Typography>

                                            <Typography variant="body2" color="text.secondary">
                                                {floorplanGroupingEnabled
                                                    ? 'This project uses multi-building organization. Drawings are grouped by building.'
                                                    : 'This project does not use multi-building organization.'}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontStyle: 'italic',
                                                    mt: 0.5,
                                                }}
                                            >
                                                Building configuration is set during project
                                                creation.
                                            </Typography>

                                            {floorplanGroupingEnabled &&
                                                buildingNames.length > 0 && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: 1,
                                                            mt: 1.5,
                                                        }}
                                                    >
                                                        {buildingNames.map((name) => (
                                                            <Chip
                                                                key={name}
                                                                label={name}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                        </Box>
                                    </Box>
                                )}

                                {/* Section 2: Access Control - gated by PROJECT_WRITE */}
                                {canWriteProject && (
                                    <Box
                                        sx={{
                                            backgroundColor: '#fff',
                                            borderRadius: '12px',
                                            p: { xs: 3, sm: 4 },
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                            border: '1px solid #f0f0f0',
                                        }}
                                    >
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1,
                                                color: '#0a0b0d',
                                                fontSize: '1.25rem',
                                            }}
                                        >
                                            Access Control
                                        </Typography>

                                        {/* Share Internal Deficiencies Setting */}
                                        <Box
                                            sx={{
                                                py: 2,
                                                borderBottom: '1px solid #f0f0f0',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-start',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 600, mb: 0.5 }}
                                            >
                                                Share Internal Deficiencies
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {isShareWithExternal
                                                    ? 'External Project Members can view internally created deficiencies.'
                                                    : 'External Project Members can NOT view internally created deficiencies.'}
                                            </Typography>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={isShareWithExternal}
                                                        onChange={handleShareWithExternalToggle}
                                                        color="primary"
                                                        disabled={isSaving}
                                                    />
                                                }
                                                label={isShareWithExternal ? 'Enabled' : 'Disabled'}
                                                sx={{ m: 0 }}
                                            />
                                        </Box>

                                        {/* External Submissions Setting */}
                                        <Box
                                            sx={{
                                                py: 2,
                                                borderBottom: isAllowExternalSubmissions
                                                    ? '1px solid #f0f0f0'
                                                    : 'none',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-start',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 600, mb: 0.5 }}
                                            >
                                                External Member Submissions
                                            </Typography>

                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={isAllowExternalSubmissions}
                                                        onChange={
                                                            handleAllowExternalSubmissionsToggle
                                                        }
                                                        color="primary"
                                                        disabled={isSaving}
                                                    />
                                                }
                                                label={
                                                    isAllowExternalSubmissions
                                                        ? 'Enabled'
                                                        : 'Disabled'
                                                }
                                                sx={{ m: 0 }}
                                            />

                                            <Typography variant="body2" color="text.secondary">
                                                {isAllowExternalSubmissions
                                                    ? 'External Project Members can submit new deficiencies.'
                                                    : 'External Project Members CANNOT submit new deficiencies.'}
                                            </Typography>

                                            {/* Submission Deadline - only show when external submissions are allowed */}
                                            {isAllowExternalSubmissions && (
                                                <Box sx={{ mt: 2 }}>
                                                    <TextField
                                                        type="date"
                                                        label="Deadline Date"
                                                        value={externalSubmissionDeadline}
                                                        onChange={handleDeadlineDateChange}
                                                        disabled={isSaving}
                                                        fullWidth
                                                        size="small"
                                                        slotProps={{
                                                            inputLabel: {
                                                                shrink: true,
                                                            },
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mb: 2 }}
                                                    >
                                                        External members can submit deficiencies
                                                        until this date
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {/* Section 3: Deficiency Defaults - PM/Admin only */}
                                {isPMOrAdmin && canWriteProject && (
                                    <Box
                                        sx={{
                                            backgroundColor: '#fff',
                                            borderRadius: '12px',
                                            p: { xs: 3, sm: 4 },
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                            border: '1px solid #f0f0f0',
                                        }}
                                    >
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1,
                                                color: '#0a0b0d',
                                                fontSize: '1.25rem',
                                            }}
                                        >
                                            Deficiency Defaults
                                        </Typography>

                                        {/* Auto Due Date Setting */}
                                        <Box
                                            sx={{
                                                py: 2,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-start',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 600, mb: 0.5 }}
                                            >
                                                Auto Due Date
                                            </Typography>

                                            <Typography variant="body2" color="text.secondary">
                                                Automatically assign a due date to deficiencies when
                                                they are assigned.
                                            </Typography>

                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={autoDueDateEnabled}
                                                        onChange={handleAutoDueDateToggle}
                                                        color="primary"
                                                        disabled={isSaving}
                                                    />
                                                }
                                                label={autoDueDateEnabled ? 'Enabled' : 'Disabled'}
                                                sx={{ m: 0 }}
                                            />

                                            {/* Days input - only show when enabled */}
                                            {autoDueDateEnabled && (
                                                <Box
                                                    sx={{
                                                        mt: 2,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.primary"
                                                        sx={{ fontWeight: 500 }}
                                                    >
                                                        Due
                                                    </Typography>
                                                    <TextField
                                                        type="number"
                                                        value={autoDueDateDays}
                                                        onChange={handleAutoDueDateDaysChange}
                                                        disabled={isSaving}
                                                        size="small"
                                                        placeholder="5"
                                                        slotProps={{
                                                            htmlInput: {
                                                                min: 1,
                                                                max: 365,
                                                                style: {
                                                                    textAlign: 'center',
                                                                },
                                                            },
                                                        }}
                                                        sx={{ width: 80 }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        color="text.primary"
                                                        sx={{ fontWeight: 500 }}
                                                    >
                                                        days after assignment
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {/* Section 4: Notification Preferences */}
                                {canManageNotificationPreferences && (
                                    <Box
                                        sx={{
                                            backgroundColor: '#fff',
                                            borderRadius: '12px',
                                            p: { xs: 3, sm: 4 },
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                            border: '1px solid #f0f0f0',
                                        }}
                                    >
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1,
                                                color: '#0a0b0d',
                                                fontSize: '1.25rem',
                                            }}
                                        >
                                            {currentUserName
                                                ? `${currentUserName}'s Notification Preferences`
                                                : 'Notification Preferences'}
                                        </Typography>

                                        {project?.id && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                <NotificationProvider projectId={project.id}>
                                                    <EnhancedNotificationPreferences />
                                                </NotificationProvider>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {tabs[tabValue]?.id === 'manage-users' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <ManageUsers
                                    projectId={projectId}
                                    canWriteMembers={canWriteMembers}
                                />
                            </Box>
                        )}

                        {tabs[tabValue]?.id === 'field-workers' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <FieldWorkerRequests
                                    projectId={projectId}
                                    canWrite={canWriteFieldWorkerRequests}
                                    canReview={canReviewFieldWorkerRequests}
                                />
                            </Box>
                        )}
                    </Grid2>
                </Grid2>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={5000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {/* Floating Save Indicator - visible when scrolled */}
                <Fade in={isSaving || showSaved} timeout={300}>
                    <FloatingSaveIndicator elevation={0}>
                        {isSaving && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={16} sx={{ mr: 1, color: '#1652f0' }} />
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.875rem' }}
                                >
                                    Saving...
                                </Typography>
                            </Box>
                        )}

                        {showSaved && !isSaving && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'success.main',
                                }}
                            >
                                <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        color: 'success.main',
                                    }}
                                >
                                    Saved
                                </Typography>
                            </Box>
                        )}
                    </FloatingSaveIndicator>
                </Fade>
            </Box>
        </PageSaveContext.Provider>
    );
};

// Enhanced NotificationPreferences that uses the page's unified save state
const EnhancedNotificationPreferences = () => {
    const pageContext = useContext(PageSaveContext);
    const { isUpdatingPreferences } = useNotifications();

    // Report notification save state to parent page
    useEffect(() => {
        if (pageContext) {
            pageContext.updateSaveState(isUpdatingPreferences);
        }
    }, [isUpdatingPreferences, pageContext]);

    return (
        <NotificationPreferences
            hideSaveIndicators={true}
            hideAutoSaveMessage={true}
            hideHeading={true}
        />
    );
};

export default ProjectSettingsPage;
